import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KCard } from "@/components/KCard";
import { ProgressBar } from "@/components/ProgressBar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import {
  getCourseChapters,
  getLesson,
  getMyEnrollments,
  updateCourseProgress,
  getCourseProgress,
  markLessonComplete,
  type ChapterDetails,
  type LessonDetails,
} from "@/api/lms/courses";
import { getCourseExams, ExamTypeBackend } from "@/api/lms/exams";
import { SecureVideoPlayer } from "@/components/SecureVideoPlayer";
import {
  ChevronLeft,
  Clock,
  ExternalLink,
  FileText,
  Menu,
  Music,
  PlayCircle,
  Radio,
  Settings,
  Video,
  X,
  Lock,
} from "lucide-react";

type FlatLesson = {
  chapterId: string;
  chapterTitle: string;
  lesson: LessonDetails;
  chapterIndex: number;
  lessonIndex: number;
};

function flattenAllLessons(chapters: ChapterDetails[]): FlatLesson[] {
  const out: FlatLesson[] = [];
  chapters.forEach((ch, chapterIndex) => {
    (ch.lessons ?? []).forEach((lesson, lessonIndex) => {
      out.push({
        chapterId: ch.id,
        chapterTitle: ch.title,
        lesson,
        chapterIndex,
        lessonIndex,
      });
    });
  });
  return out;
}

function lessonHref(courseId: string, chapterId: string, lessonId: string) {
  return `/lesson?courseId=${encodeURIComponent(courseId)}&chapterId=${encodeURIComponent(chapterId)}&lessonId=${encodeURIComponent(lessonId)}`;
}

function formatSchedule(isoStart?: string | null, isoEnd?: string | null) {
  if (!isoStart || !isoEnd) return null;
  const s = new Date(isoStart);
  const e = new Date(isoEnd);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  return `${s.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })} – ${e.toLocaleTimeString(undefined, { timeStyle: "short" })}`;
}

export default function LessonView() {
  const { user, isHydrating } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const courseId = searchParams.get("courseId") ?? "";
  const chapterId = searchParams.get("chapterId") ?? "";
  const lessonId = searchParams.get("lessonId") ?? "";

  const isStudent = !user || user?.backendRole === "STUDENT";
  const myCoursesPath = user?.backendRole === "TEACHER" ? "/teacher/courses" : "/courses";

  const enrollmentsQuery = useQuery({
    queryKey: ["myEnrollmentsLessonView"],
    queryFn: getMyEnrollments,
    enabled: Boolean(user) && isStudent,
  });

  const isEnrolled = useMemo(() => {
    if (!user) return false;
    if (user.backendRole === "TEACHER" || user.backendRole === "SYSTEM_ADMIN") return true;
    if (!courseId || !enrollmentsQuery.data) return false;
    return enrollmentsQuery.data.some((e) => e.course?.id === courseId);
  }, [user, courseId, enrollmentsQuery.data]);

  const chaptersQuery = useQuery({
    queryKey: ["courseChapters", courseId],
    queryFn: () => getCourseChapters(courseId),
    enabled: Boolean(courseId),
  });

  const courseExamsQuery = useQuery({
    queryKey: ["courseExamsForLessonView", courseId],
    queryFn: () => getCourseExams(courseId, { page: 1, limit: 100 }),
    enabled: Boolean(courseId),
  });

  const lessonQuery = useQuery({
    queryKey: ["lesson", courseId, chapterId, lessonId],
    queryFn: () => getLesson(courseId, chapterId, lessonId),
    enabled: Boolean(courseId && chapterId && lessonId),
  });

  const flatLessons = useMemo(() => flattenAllLessons(chaptersQuery.data ?? []), [chaptersQuery.data]);

  const currentIndex = useMemo(
    () => flatLessons.findIndex((x) => x.lesson.id === lessonId && x.chapterId === chapterId),
    [flatLessons, lessonId, chapterId],
  );

  const prev = currentIndex > 0 ? flatLessons[currentIndex - 1] : null;
  const rawNext = currentIndex >= 0 && currentIndex < flatLessons.length - 1 ? flatLessons[currentIndex + 1] : null;

  /**
   * If the current lesson is the LAST lesson in its chapter AND that chapter
   * has a recorded (barrier) exam, the "next" action should go to the exam —
   * not straight into the next chapter's first lesson.
   */
  const chapterExamNext = useMemo(() => {
    if (!rawNext) return null;                         // already the last lesson overall
    if (rawNext.chapterId === chapterId) return null;  // next lesson is still in the same chapter
    // The next item belongs to a different chapter → check if current chapter has a barrier exam
    const exams = courseExamsQuery.data?.items ?? [];
    const exam = exams.find(
      (e) => e.type === ExamTypeBackend.recorded && e.chapterId === chapterId,
    );
    return exam ?? null;
  }, [rawNext, chapterId, courseExamsQuery.data]);

  // If there's a chapter exam to take first, suppress the lesson-to-lesson "next"
  const next = chapterExamNext ? null : rawNext;

  // Removed direct redirect blocks to allow guests to preview lesson metadata

  useEffect(() => {
    if (!courseId || !chapterId || !lessonId || !user || !isStudent || !isEnrolled) return;
    let cancelled = false;
    void (async () => {
      try {
        await updateCourseProgress(courseId, { chapterId });
        if (!cancelled) {
          queryClient.invalidateQueries({ queryKey: ["courseProgress", courseId] });
          queryClient.invalidateQueries({ queryKey: ["myProgress"] });
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, chapterId, lessonId, user, isStudent, isEnrolled, queryClient]);

  const totalLessons = flatLessons.length;
  const progressPct =
    totalLessons > 0 && currentIndex >= 0 ? Math.round(((currentIndex + 1) / totalLessons) * 100) : 0;

  const lesson = lessonQuery.data;
  const loadingLesson = lessonQuery.isPending;
  const lessonError = lessonQuery.isError;
  const progressQuery = useQuery({
    queryKey: ["courseProgress", courseId],
    queryFn: () => getCourseProgress(courseId),
    enabled: Boolean(courseId) && isStudent && isEnrolled,
  });

  const progress = progressQuery.data;
  const currentChapterId = progress?.currentChapterId ?? null;

  const rawChapters = chaptersQuery.data ?? [];
  const chapterRows = useMemo(() => {
    if (!rawChapters.length) return [];
    if (user && (user.backendRole === "TEACHER" || user.backendRole === "SYSTEM_ADMIN")) {
      return rawChapters.map((ch) => ({ ...ch, isLocked: false }));
    }
    if (!isEnrolled) {
      return rawChapters.map((ch, idx) => ({ ...ch, isLocked: idx > 0 }));
    }
    const activeIndex = currentChapterId
      ? rawChapters.findIndex((c) => c.id === currentChapterId)
      : 0;
    const resolvedActiveIndex = activeIndex === -1 ? 0 : activeIndex;
    return rawChapters.map((ch, idx) => ({
      ...ch,
      isLocked: idx > resolvedActiveIndex,
    }));
  }, [rawChapters, user, isEnrolled, currentChapterId]);

  const currentChapter = useMemo(() => {
    return (chaptersQuery.data ?? []).find((ch) => ch.id === chapterId) ?? null;
  }, [chaptersQuery.data, chapterId]);

  const bypassLock = user && (user.backendRole === "TEACHER" || user.backendRole === "SYSTEM_ADMIN");

  const isCurrentChapterLocked = useMemo(() => {
    if (bypassLock) return false;
    const ch = chapterRows.find((c) => c.id === chapterId);
    return ch ? Boolean(ch.isLocked) : false;
  }, [chapterRows, chapterId, bypassLock]);

  const showPremiumLock =
    ((!isEnrolled && !lesson?.isPreview) || isCurrentChapterLocked) && !bypassLock;

  if (user && isStudent && enrollmentsQuery.isPending) {
    return (
      <div className="flex h-screen items-center justify-center bg-card p-6">
        <Skeleton className="h-64 w-full max-w-lg rounded-xl" />
      </div>
    );
  }

  if (!courseId || !chapterId || !lessonId) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-card p-6">
        <div className="dash-empty-state max-w-sm w-full">
          <p className="text-sm font-medium mb-4">Choose a lesson from your course.</p>
          <Button asChild>
            <Link to={myCoursesPath}>My courses</Link>
          </Button>
        </div>
      </div>
    );
  }

  const renderSidebarContent = (isMobileInline: boolean) => {
    if (chaptersQuery.isPending) {
      return <Skeleton className="h-40 w-full" />;
    }

    return (
      <div className="space-y-4">
        {chapterRows.map((ch, chapterIndex) => {
          const exam =
            (courseExamsQuery.data?.items ?? []).find(
              (e) => e.type === ExamTypeBackend.recorded && e.chapterId === ch.id,
            ) ?? null;

          const chapterLessons = ch.lessons ?? [];
          const completedLessons = progress?.completedLessons ?? [];
          const allCompleted = chapterLessons.length > 0 && chapterLessons.every((l) => completedLessons.includes(l.id));
          const isExamLocked = !allCompleted && !bypassLock;

          return (
            <div key={ch.id}>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                {ch.isLocked && <Lock size={12} className="text-muted-foreground shrink-0" />}
                {ch.title}
              </p>

              <ul className="space-y-1">
                {(ch.lessons ?? []).map((l) => {
                  const active = l.id === lessonId && ch.id === chapterId;
                  const isPlayable = (isEnrolled && !ch.isLocked) || l.isPreview || bypassLock;

                  const isCompleted = progress?.completedLessons?.includes(l.id);

                  return (
                    <li key={l.id}>
                      <Link
                        to={isPlayable ? lessonHref(courseId, ch.id, l.id) : "#"}
                        onClick={(e) => {
                          setSidebarOpen(false);
                          if (!isPlayable) {
                            e.preventDefault();
                            toast.warning("Please complete and pass the previous chapter's exam to unlock this content.");
                          }
                        }}
                        className={cn(
                          "flex items-center gap-2 rounded-lg p-2 text-sm transition-colors",
                          active ? "bg-primary/10 text-primary font-semibold" : "text-foreground hover:bg-secondary",
                          !isPlayable && "opacity-85"
                        )}
                      >
                        {active ? (
                          <PlayCircle size={14} className="shrink-0 text-primary animate-pulse" />
                        ) : isCompleted ? (
                          <div className="w-3.5 h-3.5 shrink-0 rounded-full bg-success/20 flex items-center justify-center border border-success">
                             <div className="w-1.5 h-1.5 rounded-full bg-success" />
                          </div>
                        ) : l.type === "VIDEO" ? (
                          <Video size={14} className="shrink-0 opacity-70" />
                        ) : l.type === "PDF" ? (
                          <FileText size={14} className="shrink-0 opacity-70" />
                        ) : l.type === "AUDIO" ? (
                          <Music size={14} className="shrink-0 opacity-70" />
                        ) : (
                          <Radio size={14} className="shrink-0 opacity-70" />
                        )}
                        <span className="truncate flex-1">{l.title}</span>
                        {!isPlayable && (
                          <Lock size={11} className="text-muted-foreground shrink-0" />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {/* Chapter barrier exam for this chapter */}
              <div className="border border-border/60 rounded-xl p-3 bg-secondary/20 mt-2">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">

                      Chapter {chapterIndex + 1} exam
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {exam
                        ? !isEnrolled
                          ? "Enroll to unlock exam"
                          : isExamLocked
                          ? "Complete all lessons to unlock."
                          : "Interactive exam active."
                        : "Not created yet."}
                    </p>
                  </div>
                  {exam ? (
                    <Button
                      size="sm"
                      variant={!isEnrolled || isExamLocked ? "outline" : "secondary"}
                      className={cn("w-full sm:w-auto shrink-0", (!isEnrolled || isExamLocked) && "opacity-80 cursor-not-allowed")}
                      asChild={isEnrolled && !isExamLocked}
                      disabled={!isEnrolled || isExamLocked}
                    >
                      {!isEnrolled || isExamLocked ? (
                        <span>Locked</span>
                      ) : (
                        <Link
                          to={`/exam?courseId=${encodeURIComponent(courseId)}&examId=${encodeURIComponent(exam.id)}`}
                          onClick={() => setSidebarOpen(false)}
                        >
                          Give exam
                        </Link>
                      )}
                    </Button>
                  ) : (
                    <Button size="sm" variant="secondary" className="w-full sm:w-auto shrink-0" disabled>
                      Give exam
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {isMobileInline && chapterRows.length === 0 && (
          <div className="dash-empty-state py-8 bg-transparent shadow-none border-none">
            <p className="text-sm font-medium">No chapter content yet.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-card overflow-hidden">
      <div className="flex-1 flex flex-col overflow-y-auto">
        <header className="px-4 md:px-8 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to={`/course?courseId=${encodeURIComponent(courseId)}`}
              className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <ChevronLeft size={20} />
            </Link>
            <button type="button" onClick={() => setSidebarOpen(true)} className="lg:hidden text-muted-foreground shrink-0">
              <Menu size={20} />
            </button>
            <div className="min-w-0 flex-1 hidden sm:block">
              <p className="text-xs text-muted-foreground truncate">Lesson</p>
              <p className="text-sm font-bold text-foreground truncate">{lesson?.title ?? "…"}</p>
            </div>
          </div>
          <div className="flex-1 max-w-md mx-2 md:mx-8 hidden sm:block">
            <ProgressBar progress={progressPct} />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-bold text-muted-foreground hidden md:inline">
              {currentIndex >= 0 ? `${currentIndex + 1} / ${totalLessons || "-"}` : "-"}
            </span>
            <Button variant="ghost" size="icon" type="button" className="hidden sm:flex">
              <Settings size={16} />
            </Button>
          </div>
        </header>

        <main className="max-w-3xl mx-auto w-full p-4 sm:p-6 md:p-10 pb-24">
          {loadingLesson ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-3/4 rounded-lg" />
              <Skeleton className="aspect-video w-full rounded-2xl" />
            </div>
          ) : lessonError || !lesson ? (
            <div className="dash-empty-state py-10 border-destructive/30 bg-destructive/5 max-w-md mx-auto mt-10">
              <p className="text-sm font-medium text-destructive mb-4">Could not load this lesson. It may be locked or unavailable.</p>
              <Button variant="secondary" asChild>
                <Link to={`/course?courseId=${encodeURIComponent(courseId)}`}>Back to course</Link>
              </Button>
            </div>
          ) : (
            <>
              <p className="text-xs font-bold text-primary uppercase tracking-widest mb-2">{lesson.type}</p>
              <h1 className="text-2xl md:text-3xl font-bold mb-6 text-foreground">{lesson.title}</h1>

              {showPremiumLock ? (
                <>
                  {lesson.type === "VIDEO" && (
                    <div className="relative mb-8 rounded-2xl overflow-hidden border border-border bg-muted/20 aspect-video flex flex-col items-center justify-center p-6 text-center space-y-4 shadow-md">
                      <div className="absolute inset-0 bg-cover bg-center filter blur-md opacity-30 bg-[url('https://images.unsplash.com/photo-1542831371-29b0f74f9713?q=80&w=640')]" />
                      
                      <div className="z-10 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary relative">
                        <Lock size={20} />
                      </div>
                      <div className="z-10 space-y-1.5 max-w-md">
                        <h4 className="text-sm font-bold text-foreground">Premium Video Locked</h4>
                        <p className="text-xs text-muted-foreground">
                          Enroll or unlock this chapter to play the HD video explanation and access interactive notes.
                        </p>
                      </div>
                    </div>
                  )}

                  {lesson.type === "AUDIO" && (
                    <div className="relative mb-8 rounded-2xl border border-border bg-muted/15 p-5 flex flex-col gap-4 shadow-sm">
                      <div className="flex items-center gap-3">
                        <Button size="sm" variant="outline" disabled className="h-9 w-9 rounded-full p-0">
                          <PlayCircle size={16} className="text-muted-foreground" />
                        </Button>
                        <div className="flex-1 flex gap-1 items-end h-8">
                          {[12, 24, 16, 8, 20, 28, 14, 22, 10, 18, 30, 24, 16, 12, 26, 14, 8, 18, 22, 12].map((h, i) => (
                            <div key={i} className="flex-1 bg-muted-foreground/30 rounded-t" style={{ height: `${h}px` }} />
                          ))}
                        </div>
                        <span className="text-xs font-mono text-muted-foreground">0:00 / --:--</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded-lg">
                        <span className="flex items-center gap-1.5"><Lock size={12} /> Listening practice locked</span>
                      </div>
                    </div>
                  )}

                  {lesson.type === "PDF" && (
                    <div className="relative mb-8 rounded-2xl overflow-hidden border border-border bg-muted/20 aspect-[4/3] flex flex-col items-center justify-center p-6 text-center space-y-4 shadow-md">
                      <div className="absolute inset-4 border border-border bg-card filter blur-[6px] opacity-40 flex flex-col p-4 space-y-3">
                        <div className="h-4 bg-muted w-1/3 rounded" />
                        <div className="h-3 bg-muted w-full rounded" />
                        <div className="h-3 bg-muted w-5/6 rounded" />
                        <div className="h-3 bg-muted w-full rounded" />
                      </div>
                      <div className="z-10 w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-600 relative">
                        <FileText size={20} />
                      </div>
                      <div className="z-10 space-y-1.5 max-w-sm">
                        <h4 className="text-sm font-bold text-foreground">PDF Preview Locked</h4>
                        <p className="text-xs text-muted-foreground">
                          Unlock this chapter to read and download the complete study resources.
                        </p>
                      </div>
                    </div>
                  )}

                  {lesson.type === "LIVE" && (
                    <div className="relative mb-8 rounded-2xl border border-border bg-muted/15 p-6 space-y-4 text-center">
                      <div className="mx-auto w-12 h-12 rounded-full bg-success/10 flex items-center justify-center text-success">
                        <Radio size={20} />
                      </div>
                      <div className="space-y-1.5">
                        <h4 className="text-sm font-bold text-foreground">Live Class Locked</h4>
                        <p className="text-xs text-muted-foreground">
                          Only enrolled students can join live classes and interact with instructors.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Conversion CTA Card */}
                  <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8 text-center space-y-4 shadow-sm relative overflow-hidden mb-8">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
                    <h3 className="text-lg font-bold text-foreground">
                      {!user || !isEnrolled ? "Enroll to Unlock this Chapter" : "Unlock this Chapter to Continue"}
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      Join Mehul LMS to access all lesson materials, complete chapter assessments, and earn certificates of completion.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xs mx-auto pt-2">
                      {!user ? (
                        <Button
                          className="w-full font-bold"
                          onClick={() => navigate(`/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                        >
                          Login / Register
                        </Button>
                      ) : !isEnrolled ? (
                        <Button
                          className="w-full font-bold"
                          onClick={() => navigate(`/course?courseId=${courseId}`)}
                        >
                          Enroll in Course
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => navigate(`/course?courseId=${courseId}`)}
                        >
                          Go to Course Syllabus
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {lesson.type === "VIDEO" && (
                    <div className="mb-8 shadow-lg">
                      <SecureVideoPlayer
                        key={lessonId}
                        lessonId={lessonId}
                        courseId={courseId}
                        chapterId={chapterId}
                      />
                    </div>
                  )}

                  {lesson.type === "AUDIO" && lesson.contentUrl && (
                    <div className="mb-8 overflow-hidden rounded-2xl border border-border bg-background shadow-lg p-4">
                      <audio src={lesson.contentUrl} controls className="w-full" />
                    </div>
                  )}

                  {lesson.type === "AUDIO" && !lesson.contentUrl && (
                    <div className="dash-empty-state py-12 mb-8 bg-muted/10 border-dashed">
                      <p className="text-sm font-medium">No audio file has been attached to this lesson yet.</p>
                    </div>
                  )}

                  {lesson.type === "PDF" && lesson.contentUrl && (
                    <div className="mb-8 space-y-3">
                      <div className="aspect-[4/3] min-h-[480px] overflow-hidden rounded-2xl border border-border bg-background shadow-lg">
                        <iframe title="PDF" src={lesson.contentUrl} className="h-full w-full" />
                      </div>
                      <Button variant="outline" size="sm" asChild>
                        <a href={lesson.contentUrl} target="_blank" rel="noreferrer">
                          <ExternalLink size={14} className="mr-2" />
                          Open PDF in new tab
                        </a>
                      </Button>
                    </div>
                  )}

                  {lesson.type === "PDF" && !lesson.contentUrl && (
                    <div className="dash-empty-state py-12 mb-8 bg-muted/10 border-dashed">
                      <p className="text-sm font-medium">No PDF has been attached to this lesson yet.</p>
                    </div>
                  )}

                  {lesson.type === "LIVE" && (
                    <KCard className="mb-8 space-y-4 p-6">
                      <div className="flex items-center gap-2 text-foreground font-semibold">
                        <Radio className="text-success" size={20} />
                        Live class
                      </div>
                      {formatSchedule(lesson.liveStartAt, lesson.liveEndAt) && (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock size={16} />
                          {formatSchedule(lesson.liveStartAt, lesson.liveEndAt)}
                        </p>
                      )}
                      {lesson.contentUrl ? (
                        <Button asChild size="lg" className="w-full sm:w-auto">
                          <a href={lesson.contentUrl} target="_blank" rel="noreferrer">
                            <ExternalLink size={16} className="mr-2" />
                            Join meeting
                          </a>
                        </Button>
                      ) : (
                        <p className="text-sm text-muted-foreground">Meeting link will appear here when your instructor adds it.</p>
                      )}
                    </KCard>
                  )}
                </>
              )}

              {/* Lesson Description & Resource Metadata */}
              <div className="space-y-6 border-t border-border pt-8 mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <h3 className="text-xl font-bold text-foreground">Lesson Overview</h3>
                    <p className="text-xs text-muted-foreground">
                      Chapter: {currentChapter?.title || "Syllabus"} • Duration: {lesson.duration || 15} mins • Instructor notes attached below.
                    </p>
                  </div>
                  <div className="text-xs text-muted-foreground font-medium bg-secondary px-3 py-1.5 rounded-full self-start md:self-auto">
                    Type: <span className="text-foreground font-bold uppercase">{lesson.type}</span>
                  </div>
                </div>

                <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-wrap">
                  {lesson.description || "In this lesson, we will explore core concepts, review grammar structures, and practice listening comprehension to reinforce your JLPT skills."}
                </p>

                <div className="pt-4">
                  <div className="space-y-3">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Learning Objectives</h4>
                    <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1.5">
                      {lesson.objectives ? (
                        lesson.objectives.split('\n').filter(Boolean).map((obj, oIdx) => (
                          <li key={oIdx}>{obj}</li>
                        ))
                      ) : (
                        <>
                          <li>Understand key terminology and contexts.</li>
                          <li>Compare grammar patterns in various scenarios.</li>
                          <li>Apply acquired language rules in simulated CBT exams.</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-8 border-t border-border">
                {prev ? (
                  <Button variant="ghost" asChild className="justify-start">
                    <Link to={lessonHref(courseId, prev.chapterId, prev.lesson.id)}>← Previous</Link>
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    size="lg"
                    onClick={async () => {
                      if (isEnrolled && isStudent) {
                        try {
                          await markLessonComplete(courseId, lessonId);
                          queryClient.invalidateQueries({ queryKey: ["courseProgress", courseId] });
                          toast.success("Lesson marked as complete!");
                        } catch (err) {
                          console.error(err);
                        }
                      }
                      if (next) {
                        // Next lesson in the same chapter
                        navigate(lessonHref(courseId, next.chapterId, next.lesson.id));
                      } else if (chapterExamNext) {
                        // Last lesson in the chapter → go to chapter exam first
                        navigate(
                          `/exam?courseId=${encodeURIComponent(courseId)}&examId=${encodeURIComponent(chapterExamNext.id)}`,
                        );
                      } else {
                        navigate(`/course?courseId=${encodeURIComponent(courseId)}`);
                      }
                    }}
                  >
                    {isEnrolled && isStudent
                      ? next
                        ? "Mark as Complete & Continue →"
                        : chapterExamNext
                        ? "Mark as Complete & Take Chapter Exam →"
                        : "Mark as Complete ✓"
                      : next
                      ? "Next lesson →"
                      : chapterExamNext
                      ? "Take Chapter Exam →"
                      : "Back to course"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-foreground/20 z-40 lg:hidden"
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={cn(
          "fixed lg:relative right-0 top-0 h-full w-80 max-w-[85vw] border-l border-border bg-background overflow-y-auto p-6 z-50 transition-transform duration-300 shadow-xl lg:shadow-none",
          "lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0",
        )}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-foreground">Course content</h3>
          <button type="button" onClick={() => setSidebarOpen(false)} className="lg:hidden text-muted-foreground">
            <X size={18} />
          </button>
        </div>
        {renderSidebarContent(false)}
      </aside>
    </div>
  );
}
